import { readInviteToken, readInvitePreview } from "./DDZInvite";
import { getMjh5LaunchParams, MJH5_LAUNCH } from "@/config/Mjh5";

const BALANCES = ["trunk_balance", "trunk_pvp_available", "ddz_balance", "can_transfer_in", "can_transfer_out"];
const REJECTED = new Set(["INSUFFICIENT_PVP_BALANCE", "INSUFFICIENT_BALANCE", "PLAYER_IN_GAME", "INVALID_ARGUMENT", "ORDER_CONFLICT"]);

export function readWallet(info) {
  if (!info || typeof info.currency !== "string" || !info.currency.trim() ||
    BALANCES.some(key => !Number.isSafeInteger(info[key]) || info[key] < 0)) throw new Error("INVALID_RESPONSE");
  return Object.fromEntries([...BALANCES, "currency"].map(key => [key, info[key]]));
}

export function readTransferAmount(value, limit) {
  if (!/^[0-9]+$/.test(value)) throw new Error("INVALID_AMOUNT");
  const amount = Number(value);
  if (!Number.isSafeInteger(amount) || amount <= 0) throw new Error("INVALID_AMOUNT");
  if (amount > limit) throw new Error("OVER_LIMIT");
  return amount;
}

export function newTransferId() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return `ddz_${Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("")}`;
}

function validOrder(order) {
  return order && /^[a-zA-Z0-9_-]{8,80}$/.test(order.request_id) &&
    ["UP", "DOWN"].includes(order.direction) && Number.isSafeInteger(order.amount) && order.amount > 0;
}

// Persist only transfer identity. Login tokens and launch URLs never go into this store.
export class DDZWallet {
  constructor({ send, token, account, endpoint, storage, inviteToken = null, entry = "lobby", id = newTransferId, timeout = 15000, onChange = () => {} }) {
    Object.assign(this, { send, token, storage, inviteToken, entry, id, timeout, onChange });
    this.key = `ddz_wallet_pending:${encodeURIComponent(endpoint)}:${encodeURIComponent(account)}`;
    this.state = { wallet: null, amount: "", edited: false, busy: false, pending: null, error: "", completed: "",
      invite: null, inviteError: "", inviteBlocked: inviteToken !== null, inviteLaunchError: "" };
    this.validAccount = account !== undefined && account !== null && String(account) !== "";
    this.disposed = false;
    this.readPending();
  }

  emit(patch) {
    this.state = { ...this.state, ...patch };
    if (!this.disposed) this.onChange(this.state);
  }

  dispose() { this.disposed = true; }

  async initialize() {
    if (this.state.busy || this.disposed) return;
    if (this.inviteToken !== null) {
      this.emit({ busy: true, invite: null, inviteError: "", inviteLaunchError: "", inviteBlocked: true });
      try {
        readInviteToken(this.inviteToken);
        const res = await this.request("partner_ddz_invite_preview", { invite_token: this.inviteToken });
        if (!res || res.status !== true) throw new Error(res && res.errcode || "INVITE_PREVIEW_FAILED");
        if (this.disposed) return;
        this.emit({ invite: readInvitePreview(res.info), inviteBlocked: false });
      } catch (e) { this.emit({ inviteError: e.message || "INVITE_PREVIEW_FAILED", inviteBlocked: true }); }
      finally { this.emit({ busy: false }); }
      if (this.state.inviteBlocked || this.disposed) return;
    }
    await this.query();
  }

  readPending() {
    try {
      if (!this.validAccount || !this.storage) throw new Error();
      const saved = this.storage.getItem(this.key);
      const pending = saved ? JSON.parse(saved) : null;
      if (pending && !validOrder(pending)) throw new Error();
      this.emit({ pending, ...(pending ? { amount: String(pending.amount), edited: true } : {}) });
      return true;
    } catch (_) {
      this.emit({ error: "STORAGE_UNAVAILABLE", wallet: null });
      return false;
    }
  }

  savePending(order) {
    // Require durable storage before submitting a financial operation.
    this.storage.setItem(this.key, JSON.stringify(order));
    if (this.storage.getItem(this.key) !== JSON.stringify(order)) throw new Error("STORAGE_UNAVAILABLE");
    this.emit({ pending: order });
  }

  clearPending() {
    this.storage.removeItem(this.key);
    this.emit({ pending: null });
  }

  setAmount(amount) {
    if (!this.state.busy && !this.state.pending) this.emit({ amount, edited: true, error: "", completed: "" });
  }

  async request(command, params) {
    let timer;
    try {
      return await Promise.race([
        this.send(command, { token: this.token, ...params }),
        new Promise((resolve, reject) => { timer = setTimeout(() => reject(new Error("NETWORK_ERROR")), this.timeout); }),
      ]);
    } finally { clearTimeout(timer); }
  }

  applyWallet(info) {
    const wallet = readWallet(info);
    this.emit({ wallet, ...(!this.state.edited ? {
      amount: String(wallet.can_transfer_in), edited: false,
    } : {}) });
  }

  async query() {
    if (this.state.busy || this.disposed) return;
    if (!this.readPending()) return;
    this.emit({ busy: true, wallet: null, error: "", completed: "" });
    try {
      const res = await this.request("partner_ddz_wallet_query", {});
      if (!res || res.status !== true) throw new Error(res && res.errcode || "QUERY_FAILED");
      this.applyWallet(res.info);
    } catch (e) { this.emit({ wallet: null, error: e.message || "QUERY_FAILED" }); }
    finally { this.emit({ busy: false }); }
  }

  async transfer(direction, retry = false) {
    if (this.state.busy || this.disposed) return;
    if (!this.readPending()) return;
    let order = this.state.pending;
    if (retry && !order) return;
    if (!retry && order) { this.emit({ error: "ORDER_PENDING" }); return; }
    if (!order) {
      if (!this.state.wallet || !["UP", "DOWN"].includes(direction)) return;
      try {
        const amount = readTransferAmount(this.state.amount, this.state.wallet[direction === "UP" ? "can_transfer_in" : "can_transfer_out"]);
        order = { request_id: this.id(), direction, amount };
        if (!validOrder(order)) throw new Error("STORAGE_UNAVAILABLE");
        this.savePending(order);
      } catch (e) { this.emit({ error: ["INVALID_AMOUNT", "OVER_LIMIT"].includes(e.message) ? e.message : "STORAGE_UNAVAILABLE" }); return; }
    }
    this.emit({ busy: true, wallet: null, error: "", completed: "" });
    let refresh = false;
    try {
      const res = await this.request("partner_ddz_wallet_transfer", order);
      if (res && res.status === true) {
        const info = res.info;
        if (!info || !info.order_no || info.direction !== order.direction || info.amount !== order.amount) throw new Error("INVALID_RESPONSE");
        readWallet(info);
        this.clearPending();
        this.applyWallet(info);
        this.emit({ completed: order.direction });
      } else {
        const code = res && res.errcode || "ORDER_PENDING";
        if (REJECTED.has(code)) { this.clearPending(); refresh = true; }
        this.emit({ error: code });
      }
    } catch (_) { this.emit({ error: "ORDER_PENDING" }); }
    finally { this.emit({ busy: false }); }
    if (refresh && !this.disposed) {
      const error = this.state.error;
      await this.query();
      this.emit({ error: this.state.error || error });
    }
  }

  async launch() {
    if (this.state.busy || this.disposed || !this.readPending() || this.state.pending || !this.state.wallet || this.state.inviteBlocked ||
        (this.inviteToken !== null && !this.state.invite)) return null;
    this.emit({ busy: true, error: "", completed: "" });
    try {
      const target = getMjh5LaunchParams(this.entry);
      if (this.inviteToken !== null && Object.keys(target).length) throw new Error("INVALID_DDZ_ENTRY");
      if (this.inviteToken !== null && this.state.invite && this.state.invite.expires_at * 1000 <= Date.now()) {
        this.emit({ inviteBlocked: true, inviteLaunchError: "INVITE_EXPIRED" });
        throw new Error("INVITE_EXPIRED");
      }
      const res = await this.request(MJH5_LAUNCH.command, { game_uid: MJH5_LAUNCH.gameUid, ...target,
        ...(this.inviteToken !== null ? { invite_token: readInviteToken(this.inviteToken) } : {}) });
      if (!res || res.status !== true) {
        if (this.inviteToken !== null) this.emit({ inviteBlocked: true, inviteLaunchError: res && res.errcode || "LAUNCH_FAILED" });
        throw new Error(res && res.errcode || "LAUNCH_FAILED");
      }
      if (typeof res.url !== "string" || !res.url.trim()) throw new Error("LAUNCH_FAILED");
      const url = new URL(res.url);
      if (!["https:", "http:"].includes(url.protocol) || !(url.searchParams.get("ticket") || url.searchParams.get("externalTicket"))) throw new Error("LAUNCH_FAILED");
      return this.disposed ? null : res.url;
    } catch (e) { this.emit({ error: e.message || "LAUNCH_FAILED" }); return null; }
    finally { this.emit({ busy: false }); }
  }
}
